import Button from '@/common/components/base/button/Button';
import DateInput from '@/common/components/base/input/dateInput/DateInput';
import MobileInput from '@/common/components/base/input/mobileInput/MobileInput';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import MultipleCheckboxesModalInput from '@/common/components/base/input/multipleCheckboxesModalInput/MultipleCheckboxesModalInput';
import FreeZonePlateInput from '@/common/components/base/input/plate/freeZonePlateInput/FreeZonePlateInput';
import { FreeZonePlateValue } from '@/common/components/base/input/plate/freeZonePlateInput/freeZonePlateInput.type';
import VehiclePlateInput from '@/common/components/base/input/plate/vehiclePlateInput/VehiclePlateInput';
import { plateValue } from '@/common/components/base/input/plate/vehiclePlateInput/vehiclePlateInput.type';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import usePlateDesignRequest from '@/common/hooks/usePlateDesignRequest';
import useVehicleTypeRequest from '@/common/hooks/useVehicleTypeRequest';
import { fileStateDefaultQueryOption } from '@/common/queries/selections/fileStates/fileStateDefaultQueryOption';
import { InsurancesDefaultQueryOption } from '@/common/queries/selections/insurances/insurancesDefaultQueryOption';
import { FileTypeConst } from '@/common/types/Files.type';
import { FileStatesMappedType } from '@/common/types/FileStatesMappedType.type';
import { SelectionOptionsType } from '@/common/types/SelectionOptions.type';
import { AdvanceFilterFormType } from '@/modules/FileList/components/listFilterSection/advanceFilter/AdvanceFilterFormType.type';
import FileStateModal from '@/modules/FileList/components/listFilterSection/advanceFilter/components/FileStateModal/FileStateModal';
import SortModal from '@/modules/FileList/components/listFilterSection/advanceFilter/components/SortModal/SortModal';
import { FILE_LIST_LINKS } from '@/modules/FileList/fileListLinks';
import useFilterQueryParams from '@/modules/FileList/hooks/useFilterQueryParams';
import { MyFilesListQueryKey } from '@/modules/FileList/queries/myFilesList/myFilesListQuery.type';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box } from 'styled-system/jsx';

const fileTypes: SelectionOptionsType<FileTypeConst | 'all'>[] = [
  { label: 'همه', value: 'all' },
  { label: 'خسارت ثالث', value: 'property' },
  { label: 'خسارت بدنه', value: 'body' },
  { label: 'بازدید اولیه بدنه', value: 'initial' },
];
const sortTypes: SelectionOptionsType<'oldest' | 'newest'>[] = [
  { label: 'قدیمی‌ترین', value: 'oldest' },
  { label: 'جدیدترین', value: 'newest' },
];

const AdvanceFilterForm = ({
  onCloseModal,
  hasDateOrFileState = true,
}: {
  hasDateOrFileState?: boolean;
  onCloseModal: () => void;
}) => {
  const navigate = useNavigate();
  const { resetQueryParams, fileListParams, filterQueryParams } =
    useFilterQueryParams();
  const { data: FileStateData } = useQuery(fileStateDefaultQueryOption());

  const {
    vehicleTypeQuery: { data: vehicleTypeOptions },
  } = useVehicleTypeRequest({
    enabled: true,
  });
  const { data: insuranceData } = useQuery(InsurancesDefaultQueryOption());

  const {
    plateDesignQuery: { data: plateDesignOptions },
  } = usePlateDesignRequest({
    enabled: true,
  });

  const defaultForm = useMemo(() => {
    return {
      order_id: fileListParams?.order_id,
      insurance: fileListParams?.insurance ?? [],
      plate: fileListParams?.plate,
      vehicle_type:
        fileListParams?.vehicle_type ??
        vehicleTypeOptions?.find((item) => item.enName === 'car'),
      plate_design:
        fileListParams?.plate_design ??
        plateDesignOptions?.find((item) => item.value === 'country'),
      file_type: fileListParams?.file_type ?? fileTypes[0],
      phone_number: fileListParams?.phone_number,
      page: fileListParams?.page,
      page_size: fileListParams?.page_size,
      sort:
        fileListParams?.sort ??
        sortTypes?.find((item) => item.value === 'newest'),
      form_date: fileListParams?.form_date,
      to_date: fileListParams?.to_date,
      state: fileListParams?.state,
    };
  }, [vehicleTypeOptions, plateDesignOptions]);

  const methods = useForm<AdvanceFilterFormType>({
    defaultValues: defaultForm,
  });

  const formData = useWatch({ control: methods.control });

  const onSubmit = (params: AdvanceFilterFormType) => {
    const AdjustedParams: Partial<AdvanceFilterFormType> = { ...params };
    for (const key in AdjustedParams) {
      if (Object.prototype.hasOwnProperty.call(AdjustedParams, key)) {
        const typedKey = key as keyof AdvanceFilterFormType;
        if (AdjustedParams[typedKey] === '') {
          AdjustedParams[typedKey] = undefined;
        }
        if (
          Array.isArray(AdjustedParams[typedKey]) &&
          (AdjustedParams[typedKey] as any[]).length === 0
        ) {
          AdjustedParams[typedKey] = undefined;
        }
      }
    }

    const filteredParams = Object.fromEntries(
      Object.entries(AdjustedParams).filter(
        ([_, value]) => value !== undefined,
      ),
    );

    hasDateOrFileState
      ? navigate(FILE_LIST_LINKS.myFilesWithParams(filteredParams), {
          replace: true,
        })
      : navigate(FILE_LIST_LINKS.myNewFilesWithParams(filteredParams), {
          replace: true,
        });

    onCloseModal();
  };

  const reset = () => {
    methods.reset(defaultForm);
    resetQueryParams();
    onCloseModal();
  };

  useEffect(() => {
    methods.reset(defaultForm);
  }, [defaultForm]);

  const changeFileState = ({
    onClose,
    value,
  }: {
    onClose: () => void;
    value: FileStatesMappedType;
  }) => {
    onClose();

    methods.setValue(
      MyFilesListQueryKey.STATE,
      { label: value.label, value: value.value },
      {
        shouldDirty: true,
        shouldTouch: true,
      },
    );
  };
  const changeSort = ({
    onClose,
    value,
  }: {
    onClose: () => void;
    value: SelectionOptionsType<'newest' | 'oldest'>;
  }) => {
    onClose();

    methods.setValue(
      MyFilesListQueryKey.SORT,
      { label: value.label, value: value.value },
      {
        shouldDirty: true,
        shouldTouch: true,
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className={css({
          maxHeight: '80vh',
          height: '600px',
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
          justifyContent: 'space-between',
          overflowY: 'auto',
          mt: '2rem',
          scrollbarWidth: 'none',
        })}
      >
        <Box mt="2rem">
          <ModalInput
            options={fileTypes}
            defaultValue={methods.getValues('file_type')}
            name="file_type"
            label="نوع پرونده"
          />

          <TextInput direction="ltr" name="order_id" label="شناسه پرونده" />

          <MobileInput
            defaultValue=""
            required={false}
            name="phone_number"
            label="تلفن همراه(مقصر یا زیان‌دیده)"
          />

          <ModalInput
            options={vehicleTypeOptions}
            name="vehicle_type"
            label="نوع وسیله‌نقلیه"
            onChange={() => {
              methods.setValue(MyFilesListQueryKey.PLATE, undefined, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
              });
              methods.clearErrors(MyFilesListQueryKey.PLATE);
            }}
          />

          <ModalInput
            options={plateDesignOptions}
            onChange={() => {
              methods.setValue(MyFilesListQueryKey.PLATE, undefined, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
              });
              methods.clearErrors(MyFilesListQueryKey.PLATE);
            }}
            name="plate_design"
            label="طرح پلاک"
          />

          {formData[MyFilesListQueryKey.PLATE_DESIGN]?.value === 'country' &&
          formData[MyFilesListQueryKey.VEHICLE_TYPE]?.label !== 'موتورسیکلت' ? (
            <VehiclePlateInput
              onChange={(value) =>
                methods.setValue(MyFilesListQueryKey.PLATE, value, {
                  shouldDirty: true,
                  shouldTouch: true,
                })
              }
              value={fileListParams?.plate as plateValue}
            />
          ) : (
            <FreeZonePlateInput
              key={`${formData[MyFilesListQueryKey.PLATE_DESIGN]?.value}${formData[MyFilesListQueryKey.VEHICLE_TYPE]?.value}`}
              requiredError={
                methods.formState.errors?.[MyFilesListQueryKey.PLATE]?.message
              }
              vehicle_plate_design={
                formData[MyFilesListQueryKey.PLATE_DESIGN]?.value ?? 'country'
              }
              vehicle_type_id={
                formData[MyFilesListQueryKey.VEHICLE_TYPE]?.value
                  ? +formData[MyFilesListQueryKey.VEHICLE_TYPE]?.value
                  : 1
              }
              onChange={(value) =>
                methods.setValue(MyFilesListQueryKey.PLATE, value, {
                  shouldDirty: true,
                  shouldTouch: true,
                })
              }
              value={fileListParams?.plate as FreeZonePlateValue}
            />
          )}
          <Box my="1.5rem" />
          {hasDateOrFileState && (
            <ModalInput
              options={[{ label: '', value: '' }]}
              name="state"
              label="وضعیت پرونده"
            >
              {(onClose) => (
                <FileStateModal
                  onClick={(value: FileStatesMappedType) => {
                    changeFileState({ onClose, value });
                  }}
                  options={FileStateData ?? []}
                />
              )}
            </ModalInput>
          )}

          <ModalInput
            onChange={() => {
              filterQueryParams({ removedItem: MyFilesListQueryKey.SORT });
            }}
            name="sort"
            label="مرتب سازی"
          >
            {(onClose) => (
              <SortModal
                onClick={(value: SelectionOptionsType<'newest' | 'oldest'>) => {
                  changeSort({ onClose, value });
                }}
                options={sortTypes}
              />
            )}
          </ModalInput>

          {hasDateOrFileState && (
            <Box display="flex" gap="10px">
              <DateInput
                modalLabel="تاریخ شروع بازدید"
                buttonText="ثبت تاریخ شروع بازدید"
                label="تاریخ بازدید از"
                name="form_date"
              />
              <DateInput
                modalLabel="تاریخ پایان بازدید"
                buttonText="ثبت تاریخ پایان بازدید"
                label="تاریخ بازدید تا"
                name="to_date"
              />
            </Box>
          )}

          <MultipleCheckboxesModalInput
            name="insurance"
            placeholder="جستجو بیمه گر"
            defaultValue={formData.insurance}
            label="بیمه گر"
            searchOptions={{
              hasSearchAttachment: true,
              placeholder: 'جستجو بیمه گر',
            }}
            data={insuranceData ?? []}
          />
          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
            gap="10px"
            mt="10px"
            position="sticky"
            bottom="0"
            backgroundColor="gray-4"
          >
            <Button
              type="reset"
              onClick={reset}
              variant="outlined"
              color="common"
              width="50%"
            >
              حذف فیلتر
            </Button>
            <Button disabled={!methods.formState.isDirty} width="50%">
              اعمال فیلتر
            </Button>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};

export default AdvanceFilterForm;
